import React, { useEffect, useMemo } from 'react'
import { pluralizeToString } from 'lib/string/pluralize'
import LinePlaneFlyIcon from 'LuxKit/Icons/line/LinePlaneFlyIcon'
import { getDefaultAirportCode } from 'selectors/flightsSelectors'
import { connect } from 'react-redux'
import OfferBadgeLabel from 'components/Common/OfferBadgeLabel/OfferBadgeLabel'
import { getBundledWithFlightOfferDisplayPricing } from 'lib/offer/getOfferDisplayPricing'
import config from 'constants/config'
import * as Analytics from 'analytics/analytics'
import { getCheapestPackageForDuration } from '../Common/getCheapestPackageForDuration'
import PriceRowPriceCaption from 'LuxKit/PricePoints/PriceRowPriceCaption'
import PriceRowValueDiscountWithCaption from 'LuxKit/PricePoints/Value/PriceRowValueDiscountWithCaption'
import PriceRowFlightPackage from 'LuxKit/PricePoints/PriceRowFlightPackage'
import LuxPlusPriceStack from 'luxPlus/components/LuxPlusPriceStack'

interface Props {
  duration?: number;
  offer: App.Offer | App.OfferSummary;
  airportCode: string;
}

function BundledHotelPriceContent({
  duration,
  airportCode,
  offer,
}: Props) {
  const enableDurationSearch = !!config.LERE_ENABLE_CALENDAR_DURATION_FILTER
  const lowestPricePackage = useMemo<App.Package | null>(() => getCheapestPackageForDuration({
    enableDurationSearch,
    offer,
    duration,
  }), [offer, duration, enableDurationSearch])

  useEffect(() => {
    if (!Number.isFinite(duration)) {
      Analytics.trackClientEvent({
        action: `duration_not_finite::${duration}`,
        subject: 'BundledHotelPriceContent',
        category: 'logging',
        type: 'operational',
      })
    }
  }, [duration])

  if (!lowestPricePackage) {
    return null
  }

  const { price, memberPrice, value, duration: durationToDisplay } = lowestPricePackage
  const flightPrice = offer.flightPrices[airportCode]
  const result = getBundledWithFlightOfferDisplayPricing(!!offer.bundledWithFlightsOnly, flightPrice, price, memberPrice, value)

  if (result.isBundledFlightPriceDisplayable) {
    const { packagedTotal, packagedMemberTotal, totalValue, flightPrice, shouldShowValue } = result

    return (
      <div>
        {offer.badge && <OfferBadgeLabel badge={offer.badge} />}
        <PriceRowPriceCaption startIcon={<LinePlaneFlyIcon />}>
          {pluralizeToString('night', durationToDisplay)}{flightPrice && ' + flights from'}
        </PriceRowPriceCaption>
        <LuxPlusPriceStack
          size="S"
          price={Math.ceil(packagedTotal!)}
          saleUnit="total*"
          memberPrice={Math.ceil(packagedMemberTotal!)}
          offerConfig={offer.luxPlus}
        />
        {shouldShowValue && <PriceRowValueDiscountWithCaption
          size="M"
          originalValue={Math.ceil(totalValue!)}
        />}
      </div>
    )
  }

  return <PriceRowFlightPackage price={undefined} product="hotel" />
}

function mapStateToProps(state: App.State) {
  return {
    airportCode: getDefaultAirportCode(state),
  }
}

export default connect(mapStateToProps)(BundledHotelPriceContent)
